/* ===========================================================================
 * MACAJE — Ambassador Mode
 *
 * A visitor who arrives through an ambassador invite link is recognised as an
 * ambassador for the rest of their visit, across every page, without the
 * invite parameter having to stay in the URL.
 *
 * ── WHAT THIS IS ALLOWED TO DECIDE ─────────────────────────────────────────
 * PRESENTATION AND ATTRIBUTION ONLY. Never money.
 *
 * This session lives in a cookie, so anyone can forge it from a console. That
 * is fine for deciding what a page LOOKS like, and fatal for deciding what a
 * customer is CHARGED. The theme has already learned this the hard way: the
 * 2026-08-15 cart-attribute membership state was reverted on 2026-08-20
 * because "cart attributes are writable from any browser console, and an unset
 * attribute read as Member, so every visitor got 15% off without ever being a
 * member" (see the note above the cart toggle in theme.js).
 *
 * So, explicitly:
 *   MAY   hide or show UI, remember which ambassador someone came through,
 *         carry that identity into the cart as attribution
 *   MUST NOT  authorise a discount, a free product, membership, or any other
 *         monetary benefit
 *
 * Money stays where it already is: the Shopify Functions, which read the
 * app-written `luxe.discount_percentage` customer metafield or real cart
 * lines — never anything the browser can set.
 *
 * ── NOT THE AFFILIATE ?ref= SYSTEM ─────────────────────────────────────────
 * `?ref=username` is a DIFFERENT, older system that records ReferralClick rows
 * server-side and drives real commission payments. It is deliberately untouched
 * here. This uses `?invite=` precisely so the two cannot collide; a visitor can
 * carry both at once and each behaves independently.
 *
 * ── VALIDATION, HONESTLY ───────────────────────────────────────────────────
 * The token is checked for SHAPE, not authenticity: there is no server-side
 * invite-token system to check it against yet. A well-formed invented token
 * will therefore open Ambassador Mode. That is acceptable only because of the
 * rule above — the worst it buys is a different-looking storefront. If invite
 * tokens ever become real, `looksValid()` is the single place to call out to
 * them.
 * ======================================================================== */
(function () {
  'use strict';

  /** The ONE place the session length is defined. */
  var AMBASSADOR_SESSION_DURATION_DAYS = 30;

  var PARAM = 'invite';            // never 'ref' -- that belongs to affiliates
  var COOKIE = 'macaje_ambassador';
  var ATTR = 'data-ambassador-mode';

  /** Toggle for development. Also switched on by ?ambdebug=1 for one visit. */
  var AMBASSADOR_DEBUG = false;

  var MS_PER_DAY = 864e5;
  var AmbassadorModeSyncCart = function () {};

  /** Bumped by hand on every deploy. Shopify's asset ?v= hash is baked into
   *  CACHED page HTML, so a returning visitor can keep running an older copy
   *  of this file for as long as their page cache lives. Without a stamp there
   *  is no way to tell, from the browser, which build is actually executing --
   *  which cost real debugging time on 2026-09-02. Check AmbassadorMode.BUILD
   *  before trusting any conclusion about this script's behaviour. */
  var BUILD = '2026-09-02e';

  /** Cart attribution is deliberately silent (it must never break a page), so
   *  it needs somewhere to record what it did for anyone verifying it. */
  var cartSync = { attempts: 0, writes: 0, lastSkip: null, lastStatus: null, lastError: null };

  function log() {
    if (!AMBASSADOR_DEBUG) return;
    try { console.log.apply(console, ['[AmbassadorMode]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  /* ------------------------------------------------------------- cookie i/o */

  function readCookie() {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + COOKIE + '=([^;]*)'));
      return m ? JSON.parse(decodeURIComponent(m[1])) : null;
    } catch (e) { return null; }
  }

  function writeCookie(session) {
    try {
      var maxAge = Math.max(0, Math.floor((new Date(session.expires_at) - Date.now()) / 1000));
      document.cookie = COOKIE + '=' + encodeURIComponent(JSON.stringify(session)) +
        ';path=/;max-age=' + maxAge + ';samesite=lax' +
        (location.protocol === 'https:' ? ';secure' : '');
      return true;
    } catch (e) { return false; }
  }

  function dropCookie() {
    try { document.cookie = COOKIE + '=;path=/;max-age=0;samesite=lax'; } catch (e) {}
  }

  /* --------------------------------------------------------------- validity */

  /** Shape only -- see the note at the top about what this does and does not
   *  prove. Deliberately strict about characters so a token can never carry
   *  markup into the DOM or a quote into a cookie. */
  function looksValid(token) {
    return typeof token === 'string' && /^[A-Za-z0-9_-]{3,64}$/.test(token);
  }

  function isExpired(session) {
    if (!session || !session.expires_at) return true;
    var t = Date.parse(session.expires_at);
    return !isFinite(t) || Date.now() >= t;
  }

  /** A session is only usable if it is well-formed AND unexpired. Anything
   *  else is treated as absent rather than repaired, so a corrupt cookie can
   *  never half-enable the mode. */
  function usable(session) {
    return !!session && session.ambassador_session === true &&
      looksValid(session.ambassador_id) && !isExpired(session);
  }

  /* ---------------------------------------------------------------- the API */

  var AmbassadorMode = {
    BUILD: BUILD,
    SESSION_DURATION_DAYS: AMBASSADOR_SESSION_DURATION_DAYS,
    PARAM: PARAM,

    getSession: function () {
      var s = readCookie();
      if (!s) return null;
      if (!usable(s)) { this.clear(); return null; }
      return s;
    },
    isActive: function () { return this.getSession() !== null; },
    isExpired: function () { return isExpired(readCookie()); },
    getId: function () { var s = this.getSession(); return s ? s.ambassador_id : null; },
    getToken: function () { var s = this.getSession(); return s ? s.invite_token : null; },

    /** Start (or re-point) a session. Returns the session, or null if the
     *  token is malformed -- in which case any EXISTING valid session is left
     *  alone, so a bad link cannot destroy a good session. */
    activate: function (token, source) {
      if (!looksValid(token)) { log('refused malformed token', token); return null; }
      var now = new Date();
      var session = {
        ambassador_session: true,
        ambassador_id: token,
        invite_token: token,
        entered_at: now.toISOString(),
        expires_at: new Date(now.getTime() + AMBASSADOR_SESSION_DURATION_DAYS * MS_PER_DAY).toISOString(),
        source: source || 'invite'
      };
      writeCookie(session);
      try { sessionStorage.setItem(COOKIE, JSON.stringify(session)); } catch (e) {}
      applyState();
      whenReady(function () { syncCartAttributes(session); });
      log('activated', session);
      return session;
    },

    clear: function () {
      dropCookie();
      try { sessionStorage.removeItem(COOKIE); } catch (e) {}
      applyState();
      log('cleared');
    },

    /** For the debug flag and for manual QA. */
    debug: function () {
      var s = readCookie();
      return {
        build: BUILD,
        cartSync: cartSync,
        active: AmbassadorMode.isActive(),
        expired: isExpired(s),
        session: s,
        durationDays: AMBASSADOR_SESSION_DURATION_DAYS
      };
    }
  };

  /* ------------------------------------------------------- the one UI switch */

  /** Sets the flag on <html> AND <body>. <html> because this runs in <head>,
   *  before <body> exists, and the flag has to be on before first paint or the
   *  Club UI flashes; <body> because that is where the brief and any future
   *  section will look. CSS targets the attribute on either ancestor. */
  function applyState() {
    var on = AmbassadorMode.isActive();
    var root = document.documentElement;
    if (on) root.setAttribute(ATTR, 'true'); else root.removeAttribute(ATTR);
    if (document.body) {
      if (on) document.body.setAttribute(ATTR, 'true'); else document.body.removeAttribute(ATTR);
    }
  }

  /* --------------------------------------------------------- cart attribution */

  /** Layer 3: carry the identity into the cart so it survives to checkout.
   *  ATTRIBUTION ONLY -- nothing anywhere reads these to grant anything.
   *
   *  TWO THINGS LEARNED BY TESTING THIS:
   *
   *  1. Shopify silently DISCARDS attributes written to an EMPTY cart, so
   *     writing on page load did nothing at all. It only writes once the cart
   *     actually holds something.
   *  2. The first version remembered "already synced" in sessionStorage, which
   *     meant that silent failure was never retried. There is no such flag now:
   *     the cart's own attributes are the only thing consulted, so the check
   *     can never disagree with reality.
   *
   *  Re-runs on every cart:updated, the event every cart surface in this theme
   *  already re-renders from, so an add/remove re-stamps without polling. */
  function syncCartAttributes(session, knownCart) {
    if (!session) { cartSync.lastSkip = 'no-session'; return; }
    cartSync.attempts++;
    var root = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/');

    // `cart:updated` hands this function the authoritative cart it was just
    // published with, so on the common path -- every add, remove and quantity
    // change -- there is nothing to go and fetch. This used to re-read
    // /cart.js unconditionally, which cost a measured ~200ms round trip on
    // every cart mutation and then usually decided it had nothing to do
    // ('already-attributed'). A direct call with nothing in hand still reads.
    var cartPromise = knownCart
      ? Promise.resolve(knownCart)
      : fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) {
            if (!r.ok) { cartSync.lastStatus = r.status; return null; }
            return r.json();
          });

    cartPromise
      .then(function (cart) {
        if (!cart) { cartSync.lastSkip = 'cart-fetch-failed'; return; }
        if (!cart.item_count) { cartSync.lastSkip = 'cart-empty'; return; }   // see (1)
        if ((cart.attributes || {}).ambassador_id === session.ambassador_id) {
          cartSync.lastSkip = 'already-attributed'; return;
        }
        return fetch(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            attributes: {
              ambassador_id: session.ambassador_id,
              ambassador_session: 'true',
              invite_token: session.invite_token
            }
          })
        }).then(function () {
          cartSync.writes++; cartSync.lastSkip = null;
          log('cart attributed to', session.ambassador_id);
        });
      })
      .catch(function (e) {
        // Attribution is best-effort and must never block a page -- but a
        // swallowed failure that leaves no trace is how this went unnoticed.
        cartSync.lastError = String(e);
      });
  }

  /** This script is loaded NON-DEFERRED in <head> so the Club UI never flashes
   *  before the mode is known. That is right for the UI flag and wrong for the
   *  cart: a /cart.js issued that early comes back not-ok (recorded as
   *  cartSync.lastStatus), so attribution has to wait for the document. */
  function whenReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /** Runs fn once the browser is idle, so attribution never competes with the
   *  drawer's own re-render for the connection. It is best-effort bookkeeping
   *  that the shopper never sees; it has no business being on the critical
   *  path of a tap. The timeout is the ceiling, not the target -- it still
   *  runs promptly on an idle main thread. */
  function whenIdle(fn) {
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(fn, { timeout: 2000 });
    else setTimeout(fn, 250);
  }

  /** Public, so a cart surface can force a re-stamp if it ever needs to.
   *  Accepts an already-loaded cart to spare the read; omit it to re-read. */
  AmbassadorModeSyncCart = function (knownCart) {
    var s = AmbassadorMode.getSession();
    if (s) syncCartAttributes(s, knownCart);
  };

  /* ------------------------------------------------------------------ boot */

  function boot() {
    var token = null;
    try {
      var params = new URLSearchParams(location.search);
      token = params.get(PARAM);
      if (params.get('ambdebug') === '1') AMBASSADOR_DEBUG = true;
    } catch (e) {}

    if (token !== null) {
      // A malformed token must not damage an existing good session (spec 17).
      if (looksValid(token)) {
        var current = AmbassadorMode.getSession();
        if (!current || current.ambassador_id !== token) AmbassadorMode.activate(token, 'invite');
        else applyState();
      } else {
        log('ignored invalid invite token; existing session untouched');
        applyState();
      }
    } else {
      // No parameter: an existing session simply continues, and an expired or
      // corrupt one is cleared by getSession() on the way through.
      applyState();
    }

    // Attribution is retried on EVERY load that has a session, not only the
    // load that started it. Two reasons it cannot be a once-per-session thing:
    // the cart is almost always still EMPTY at the moment someone arrives on
    // the invite link (and Shopify discards attributes written to an empty
    // cart), and re-opening the invite link with a session already running
    // used to take an early-return path that skipped attribution altogether.
    whenReady(function () {
      var s = AmbassadorMode.getSession();
      if (s) syncCartAttributes(s);
    });

    if (AMBASSADOR_DEBUG) log('state', AmbassadorMode.debug());
  }

  AmbassadorMode.syncCart = function (knownCart) { AmbassadorModeSyncCart(knownCart); };

  window.AmbassadorMode = AmbassadorMode;
  boot();

  // Every cart surface in this theme re-renders from this one event, so it is
  // also the right moment to re-stamp attribution -- no polling, no wrapping
  // of the cart API.
  //
  // The event's own cart is passed straight through (no re-read), and the work
  // is deferred to idle so the ~525-570ms attribute WRITE stops racing the
  // drawer's section fetch for the connection. Attribution still lands on the
  // same cart, a moment later, which is all it has ever needed to do.
  document.addEventListener('cart:updated', function (e) {
    var known = (e && e.detail) ? e.detail.cart : null;
    whenIdle(function () { AmbassadorMode.syncCart(known); });
  });

  // <body> does not exist yet when this runs in <head>; mirror the flag onto it
  // as soon as it does, and again on bfcache restore.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyState, { once: true });
  } else {
    applyState();
  }
  window.addEventListener('pageshow', applyState);
})();
